import get from 'lodash/get'
import has from 'lodash/has'

import {
    resendCodeRequest,
    resendCodeSuccess,
    resendSignUpCodeSuccess,
    signInFailure,
    signInMfaRequest,
    signInMfaRequired,
    signInRequest,
} from 'actions/auth'
import { CerebroResourceResponse } from 'api/Cerebro/types'
import {
    checkUserNotConfirmed,
    getMfaSettingsAccessToken,
    login,
    loginMfa,
    resendSignUpCode,
} from 'api/Cerebro/unscoped/auth'
import { SegmentEventName } from 'const/monitoring/segment'
import { generateCodeSentNotification } from 'helpers/notifications'
import { getPath } from 'routing/pagePaths'
import { getLocation, navigate } from 'routing/RoutingService'
import { CognitoAccessToken, Login, Tokens } from 'types/resources/auth'
import { MfaSessionExpiredError } from 'views/Auth/Errors/MfaSessionExpiredError'
import { SmsMfaChallengeError } from 'views/Auth/Errors/SmsMfaChallengeError'

import { setCerebroAuthTokens, storeCognitoAccessToken } from '../authTokens'
import { sendTrackEvent } from '../thirdParty'

import { tryFinishSignIn } from './bootstrap'
import { dispatch } from './reduxBridge'

async function authorizeCerebro(
    email: string,
    password: string
): Promise<void> {
    const response: CerebroResourceResponse<Login> = await login(
        email,
        password
    )

    if (
        response.status === 302 &&
        has(response, ['data', 'code']) &&
        response.data.code === 'SmsChallenge'
    ) {
        const tokens = response.data.tokens as Tokens
        throw new SmsMfaChallengeError(
            'SMS Challenge required',
            email,
            password,
            tokens
        )
    }

    if (response.status !== 200) {
        if (has(response, ['data', 'error_description'])) {
            throw new Error(get(response, ['data', 'error_description']))
        } else if (has(response, ['data', 'error'])) {
            throw new Error(get(response, ['data', 'error']))
        }
    }

    const { access_token, refresh_token, id_token, raider_id_token } =
        response.data
    setCerebroAuthTokens(access_token, refresh_token, id_token, raider_id_token)
}

async function setCognitoAuthTokens(
    email: string,
    password: string
): Promise<void> {
    const response: CerebroResourceResponse<CognitoAccessToken> =
        await getMfaSettingsAccessToken(email, password)

    if (response.status === 200) {
        storeCognitoAccessToken(response.data.access_token)
    }
}

async function authorizeCerebroMfa(
    email: string,
    password: string,
    tokens: Tokens,
    code: string
): Promise<void> {
    const response: CerebroResourceResponse<Login> = await loginMfa(
        email,
        password,
        tokens,
        code
    )

    if (
        response.status === 401 &&
        has(response, ['data', 'code']) &&
        response.data.code === 'SessionExpired'
    ) {
        throw new MfaSessionExpiredError('Code Expired')
    }

    if (response.status !== 200) {
        if (has(response, ['data', 'error_description'])) {
            throw new Error(get(response, ['data', 'error_description']))
        } else if (has(response, ['data', 'error'])) {
            throw new Error(get(response, ['data', 'error']))
        }
    }

    const { access_token, refresh_token, id_token, raider_id_token } =
        response.data
    setCerebroAuthTokens(access_token, refresh_token, id_token, raider_id_token)
}

export async function signIn({
    email,
    password,
}: {
    email: string
    password: string
}): Promise<void> {
    dispatch(signInRequest({ email, password }))

    try {
        await authorizeCerebro(email, password)
        await setCognitoAuthTokens(email, password)
        await tryFinishSignIn()
        sendTrackEvent(SegmentEventName.LOGIN_SUCCESS)
    } catch (err: any) {
        if (err.name === 'SmsMfaChallengeError') {
            sendTrackEvent(SegmentEventName.LOGIN_MFA_CHALLENGE)
            dispatch(signInMfaRequired())
            const location = getLocation()
            navigate(getPath('authSignInMfaPage'), {
                state: {
                    email: err.email,
                    password: err.password,
                    tokens: err.tokens,
                    from: location?.state?.from,
                },
            })
            return
        }
        const userNotConfirmed: boolean = await checkUserNotConfirmed(email)
        if (userNotConfirmed) {
            sendTrackEvent(SegmentEventName.LOGIN_USER_NOT_CONFIRMED)
            await resendSignUpCode(email)
            dispatch(resendSignUpCodeSuccess({ email, password }))
            navigate(getPath('authVerifyEmailPage'))
        } else {
            sendTrackEvent(SegmentEventName.LOGIN_ERROR)
            dispatch(signInFailure({ error: err.message }))
        }
    }
}

export async function signInMfa({
    email,
    password,
    tokens,
    code,
}: {
    email: string
    password: string
    tokens: Tokens
    code: string
}): Promise<void> {
    dispatch(signInMfaRequest({ email, password, tokens, code }))

    try {
        await authorizeCerebroMfa(email, password, tokens, code)
        await tryFinishSignIn()
        sendTrackEvent(SegmentEventName.MFA_LOGIN_SUCCESS)
    } catch (err: any) {
        if (err.name === 'MfaSessionExpiredError') {
            sendTrackEvent(SegmentEventName.MFA_LOGIN_SESSION_EXPIRED)
            navigate(getPath('authPage'))
        }
        sendTrackEvent(SegmentEventName.MFA_LOGIN_ERROR)
        dispatch(signInFailure({ error: err.message }))
    }
}

export async function resendCode({
    email,
    password,
}: {
    email: string
    password: string
}): Promise<void> {
    dispatch(resendCodeRequest({ email, password }))

    try {
        await authorizeCerebro(email, password)
        await tryFinishSignIn()
    } catch (err: any) {
        if (err.name === 'SmsMfaChallengeError') {
            dispatch(signInMfaRequired())
            dispatch(resendCodeSuccess())
            generateCodeSentNotification(
                err.tokens?.ChallengeParameters?.CODE_DELIVERY_DESTINATION?.replace(
                    '+',
                    ''
                )
            )
            const location = getLocation()
            navigate(getPath('authSignInMfaPage'), {
                state: {
                    email: err.email,
                    password: err.password,
                    tokens: err.tokens,
                    from: location?.state?.from,
                },
            })
        } else {
            dispatch(signInFailure({ error: err.message }))
        }
    }
}
